import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardCountByZoneAndEquipmentStatus } from "./axios/dashboardAxios";
import { getEquipmentStatusColor } from "data/data";

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

function KakaoMap() {
  const [mapZones, setMapZones] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const markersRef = useRef([]);
  const isMapReadyRef = useRef(false);

  const overlayRef = useRef(null);
  const activeMarkerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isInitialBoundsSetRef = useRef(false);
  const navigate = useNavigate();

  /* ==========================================
     1️⃣ 데이터 Fetching (1분 간격)
  ========================================== */
  useEffect(() => {
    const fetchMapZones = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      try {
        const res = await getDashboardCountByZoneAndEquipmentStatus();
        const list = Array.isArray(res) ? res : [];

        if (!list.length) {
          console.warn("지도 데이터 없음");
          setMapZones({ positions: [] });
          return;
        }

        const positions = list.map((info) => ({
          content: `
            <div class="map_pin" style="width: 360px; height: 150px; padding: 20px; background: unset;">
              <div style="width: 100%; padding: 10px; border-radius: 10px; border: 1px solid #fff; color: #000;">
                <div style="border-radius:10px; width:100%; background:${getEquipmentStatusColor(info.countO1N, info.countO2I, info.countO3A, info.countO4C)?.bg};">
                  <h4 style="padding:5px 0; color: #fff; text-align: center; font-size: 18px;">${info.managementCompanyName} (${info.name})</h4>
                </div>
                <div class="kakaomap_circle" style="display: flex; align-items: center; justify-content: space-evenly; margin:10px 0 7px 0;">
                  <div class="map_pin0">
                    <span class="pin1">${info.countO1N}</span>
                    <span class="pin2">${info.countO2I}</span>
                    <span class="pin3">${info.countO3A}</span>
                  </div>
                </div>
              </div>
            </div>
          `,
          lat: info.latitude,
          lng: info.longitude,
          color: getEquipmentStatusColor(
            info.countO1N,
            info.countO2I,
            info.countO3A,
            info.countO4C
          )?.marker,
          zId: info.id,
        }));

        setMapZones({ positions });
      } catch (e) {
        console.error("Map Data Loading fail", e);
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchMapZones();

    const intervalId = setInterval(() => {
      fetchMapZones();
    }, DELAY_TIME);

    return () => clearInterval(intervalId);
  }, []);

  /* ==========================================
     2️⃣ 지도 및 클러스터러 초기화 (1회 실행)
  ========================================== */
  useEffect(() => {
    const initMap = () => {
      if (!window.kakao?.maps) return;
      if (!window.kakao.maps.MarkerClusterer) {
        console.error("MarkerClusterer not loaded");
        return;
      }

      if (isMapReadyRef.current) return;

      const container = document.getElementById("map");
      if (!container) return;

      const map = new window.kakao.maps.Map(container, {
        center: new window.kakao.maps.LatLng(37.4677980321326, 126.824175614165),
        level: 11,
      });

      const clusterer = new window.kakao.maps.MarkerClusterer({
        map: map,
        averageCenter: true,
        minLevel: 8,
        styles: [
          {
            width: "40px",
            height: "40px",
            background: "#91caff",
            color: "#000",
            borderRadius: "50%",
            lineHeight: "40px",
            textAlign: "center",
          },
          {
            width: "50px",
            height: "50px",
            background: "#5599ff",
            color: "#fff",
            borderRadius: "50%",
            lineHeight: "50px",
            textAlign: "center",
          },
          {
            width: "60px",
            height: "60px",
            background: "#0055cc",
            color: "#fff",
            borderRadius: "50%",
            lineHeight: "60px",
            textAlign: "center",
          },
        ],
      });

      // 🔹 지도 자체 이벤트는 초기화할 때 1회만 등록
      window.kakao.maps.event.addListener(map, "click", () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
          activeMarkerRef.current = null;
        }
      });

      window.kakao.maps.event.addListener(map, "dragstart", () => {
        isInitialBoundsSetRef.current = true;
      });

      mapRef.current = map;
      clustererRef.current = clusterer;
      isMapReadyRef.current = true;
      setIsMapReady(true);
    };

    if (window.kakao?.maps) {
      window.kakao.maps.load(initMap);
    } else {
      if (document.getElementById("kakao-map-sdk")) return;

      const script = document.createElement("script");
      script.id = "kakao-map-sdk";
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.REACT_APP_KAKAO_API_KEY}&autoload=false&libraries=services,clusterer`;
      script.async = true;
      script.onload = () => window.kakao.maps.load(initMap);
      document.head.appendChild(script);
    }
  }, []);

  /* ==========================================
     3️⃣ mapZones 변경 시 마커 / 클러스터러 갱신
  ========================================== */
  useEffect(() => {
    if (!isMapReady || !mapZones?.positions) return;

    // 🔹 기존 마커 및 오버레이 정리
    if (clustererRef.current) {
      clustererRef.current.clear();
    }
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
      activeMarkerRef.current = null;
    }

    if (mapZones.positions.length === 0) return;

    const imageSize = new window.kakao.maps.Size(32, 32);

    const markers = mapZones.positions.map((position) => {
      const markerImage = new window.kakao.maps.MarkerImage(
        `${process.env.PUBLIC_URL}/markers/${position.color}.svg`,
        imageSize
      );

      const marker = new window.kakao.maps.Marker({
        position: new window.kakao.maps.LatLng(position.lat, position.lng),
        image: markerImage,
      });

      // 마커 클릭 이벤트
      window.kakao.maps.event.addListener(marker, "click", () => {
        navigate(`/dashboard/sub?zId=${position.zId}`);
      });

      // 마커 마우스오버 이벤트
      window.kakao.maps.event.addListener(marker, "mouseover", () => {
        if (activeMarkerRef.current === marker) return;

        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }

        const overlay = new window.kakao.maps.CustomOverlay({
          content: position.content,
          position: marker.getPosition(),
          yAnchor: 1.2,
          zIndex: 10,
        });

        overlay.setMap(mapRef.current);
        overlayRef.current = overlay;
        activeMarkerRef.current = marker;
      });

      return marker;
    });

    clustererRef.current.addMarkers(markers);
    markersRef.current = markers;

    // 🔹 최초 1회만 마커 전체 위치에 맞춰 지도 영역 조정
    if (!isInitialBoundsSetRef.current && mapZones.positions.length > 0) {
      const bounds = new window.kakao.maps.LatLngBounds();
      mapZones.positions.forEach((position) => {
        bounds.extend(new window.kakao.maps.LatLng(position.lat, position.lng));
      });

      if (!bounds.isEmpty()) {
        if (mapZones.positions.length === 1) {
          mapRef.current.setLevel(7);
          mapRef.current.panTo(bounds.getSouthWest());
        } else {
          mapRef.current.setBounds(bounds);
        }
        isInitialBoundsSetRef.current = true;
      }
    }
  }, [isMapReady, mapZones, navigate]);

  return (
    <div>
      <div id="map" style={{ width: "100%", height: "655px", maxHeight: "655px" }}></div>
    </div>
  );
}

export default KakaoMap;